import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Search, Download, Inbox, ChevronDown, Paperclip, ExternalLink, Check, StickyNote, Mail, Send, Settings, Plug, Clock, CircleDot, FileText, RefreshCw, Users, UserCheck, Building2, Wrench, Activity, AlertTriangle, Plus, Pencil, Newspaper } from "lucide-react";
import { PROJECT_TYPES, BUDGETS, TIMELINES, CONTACT_METHODS, CITIES } from "../data";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import { NotificationBell } from "../components/site/NotificationBell";
import { AttachmentList } from "../components/site/AttachmentList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["New", "Contacted", "Connected"];
const STATUS_STYLES = {
  New: "bg-[#54BCE6] text-[#133955]",
  Contacted: "bg-[#FFE8B0] text-[#7a5b00]",
  Connected: "bg-[#C7F0D8] text-[#0f6b3d]",
};

const PROJECT_STATUS_STYLE = "bg-[#133955] text-white";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const _norm = (s) => (s || "").toLowerCase().trim();
const _areaMatch = (c, lead) => (c.service_areas || []).some((a) => {
  const x = _norm(a), y = _norm(lead.city);
  return x && y && (x.includes(y) || y.includes(x));
});
const _serviceMatch = (c, lead) => {
  const pt = _norm(lead.project_type);
  const tokens = pt.split(/[^a-z]+/).filter((t) => t.length > 3);
  return (c.services || []).some((s) => {
    const x = _norm(s);
    if (!x) return false;
    if (x.includes(pt) || pt.includes(x)) return true;
    return tokens.some((t) => x.includes(t));
  });
};
const fitInfo = (c, lead) => {
  const area = _areaMatch(c, lead), service = _serviceMatch(c, lead);
  return { area, service, best: area && service, any: area || service };
};
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Normalized list of contractors assigned to a lead (supports legacy singular fields).
const assignedOf = (l) =>
  (l.assigned_contractors?.length
    ? l.assigned_contractors
    : (l.assigned_contractor_id
      ? [{ contractor_id: l.assigned_contractor_id, contractor_ref: l.assigned_contractor_ref, contractor_name: l.assigned_contractor_name, acknowledged: l.acknowledged }]
      : []));

export default function AdminDashboard() {
  const { token, admin, role, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [addOpen, setAddOpen] = useState(false);

  const reloadLeads = async () => {
    const { data } = await axios.get(`${API}/admin/project-requests`, { headers: { Authorization: `Bearer ${token}` } });
    setLeads(data.requests || []);
  };

  const createLead = async (form, files) => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    (files || []).slice(0, 8).forEach((f) => fd.append("files", f));
    const { data } = await axios.post(`${API}/admin/project-requests`, fd, { headers: { Authorization: `Bearer ${token}` } });
    toast.success(`Lead #${String(data.lead_number).padStart(4, "0")} added.`);
    setAddOpen(false);
    await reloadLeads();
    openLead(data.id, "");
  };
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaving, setNoteSaving] = useState(null);
  const [noteSaved, setNoteSaved] = useState(null);
  const [gmail, setGmail] = useState({ configured: false, connected: false, gmail_address: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [composer, setComposer] = useState({ open: false, lead: null, subject: "", body: "", sending: false });
  const [viewMsg, setViewMsg] = useState(null);
  const [activity, setActivity] = useState({});
  const [approved, setApproved] = useState([]);
  const [assignSel, setAssignSel] = useState({});
  const [assigning, setAssigning] = useState(null);
  const [assignRole, setAssignRole] = useState({});
  const [roleEdit, setRoleEdit] = useState(null); // `${leadId}:${contractorId}` currently being edited
  const [roleDraft, setRoleDraft] = useState("");
  const [fitOnly, setFitOnly] = useState({});
  const [projectData, setProjectData] = useState({});
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!authLoading && !token) navigate("/admin/login");
    if (!authLoading && token && role && role !== "admin") navigate(role === "agent" ? "/agent" : "/portal");
  }, [authLoading, token, role, navigate]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/project-requests`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setLeads((data.requests || []).map((l) => ({ ...l, status: l.status || "New", notes: l.notes || "" })));
      } catch (err) {
        if (err?.response?.status === 401) { logout(); navigate("/admin/login"); return; }
        if (active) setError("Failed to load leads.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token, logout, navigate]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/contractors-approved`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setApproved(data.contractors || []);
      } catch (_) { /* non-blocking */ }
    })();
    return () => { active = false; };
  }, [token]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: /admin?lead=<id> opens that lead's detail straight from a notification
  useEffect(() => {
    const target = searchParams.get("lead");
    if (!target || loading) return;
    const found = leads.find((l) => l.id === target);
    if (found) {
      setQuery("");
      setStatusFilter("All");
      setOpenId(target);
      setNoteDrafts((d) => (d[target] === undefined ? { ...d, [target]: found.notes || "" } : d));
      if (!activity[target]) loadActivity(target);
      loadProjectData(target);
      setTimeout(() => {
        document.querySelector(`[data-testid="lead-row-${target}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    } else {
      toast.error("That lead could not be found.");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading]);

  const assignLead = async (id) => {
    const cid = assignSel[id];
    if (!cid) { toast.error("Choose a contractor to assign."); return; }
    setAssigning(id);
    try {
      const { data } = await axios.post(`${API}/admin/project-requests/${id}/assign`, { contractor_id: cid, role_label: (assignRole[id] || "").trim() }, authHeader);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...data.lead } : l)));
      loadProjectData(id);
      setAssignSel((s) => ({ ...s, [id]: "" }));
      setAssignRole((s) => ({ ...s, [id]: "" }));
      toast.success("Lead assigned to contractor.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not assign lead.");
    } finally { setAssigning(null); }
  };

  const unassignLead = async (id, cid) => {
    setAssigning(id);
    try {
      const { data } = await axios.post(`${API}/admin/project-requests/${id}/unassign/${cid}`, {}, authHeader);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...data.lead } : l)));
      setAssignSel((s) => ({ ...s, [id]: "" }));
      toast.success("Contractor removed from this lead.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not remove contractor.");
    } finally { setAssigning(null); }
  };

  const saveRoleLabel = async (id, cid) => {
    setAssigning(id);
    try {
      const { data } = await axios.patch(`${API}/admin/project-requests/${id}/assignments/${cid}`, { role_label: roleDraft }, authHeader);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...data.lead } : l)));
      setRoleEdit(null);
      toast.success("Role updated.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update role.");
    } finally { setAssigning(null); }
  };

  const updateStatus = async (id, status) => {
    setSavingId(id);
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await axios.patch(`${API}/admin/project-requests/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      setLeads(prev); // revert on failure
      setError("Could not update status. Try again.");
    } finally {
      setSavingId(null);
    }
  };

  const loadProjectData = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/project-requests/${id}/project`, authHeader);
      setProjectData((p) => ({ ...p, [id]: { quotes: data.quotes || [], contracts: data.contracts || [] } }));
    } catch (_) { /* non-blocking */ }
  };

  const openLead = (id, currentNotes) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    if (next === null && searchParams.get("lead")) setSearchParams({}, { replace: true });
    if (next && noteDrafts[id] === undefined) {
      setNoteDrafts((d) => ({ ...d, [id]: currentNotes || "" }));
    }
    if (next && !activity[id]) loadActivity(id);
    if (next) loadProjectData(id);
  };

  const saveNotes = async (id) => {
    setNoteSaving(id);
    setNoteSaved(null);
    const notes = noteDrafts[id] ?? "";
    try {
      await axios.patch(`${API}/admin/project-requests/${id}/notes`, { notes }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes } : l)));
      setNoteSaved(id);
      setTimeout(() => setNoteSaved((cur) => (cur === id ? null : cur)), 2500);
    } catch (err) {
      setError("Could not save notes. Try again.");
    } finally {
      setNoteSaving(null);
    }
  };

  const refreshGmail = async () => {
    try {
      const { data } = await axios.get(`${API}/gmail/status`, authHeader);
      setGmail(data);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    if (!token) return;
    refreshGmail();
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gmail");
    if (g === "connected") { toast.success("Gmail connected successfully."); refreshGmail(); }
    else if (g === "error") {
      const stage = params.get("stage") || "unknown";
      const reason = params.get("reason");
      toast.error(`Gmail connection failed at: ${stage}${reason ? ` — ${reason}` : ""}. Please try again.`, { duration: 15000 });
    }
    if (g) window.history.replaceState({}, "", "/admin");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const connectGmail = async () => {
    setGmailBusy(true);
    try {
      const { data } = await axios.get(`${API}/oauth/gmail/login`, authHeader);
      window.location.href = data.auth_url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not start Gmail connection.");
      setGmailBusy(false);
    }
  };

  const disconnectGmail = async () => {
    setGmailBusy(true);
    try {
      await axios.post(`${API}/gmail/disconnect`, {}, authHeader);
      toast.success("Gmail disconnected.");
      await refreshGmail();
    } catch (_) {
      toast.error("Could not disconnect Gmail.");
    } finally {
      setGmailBusy(false);
    }
  };

  const [syncingId, setSyncingId] = useState(null);
  const syncReplies = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await axios.post(`${API}/admin/leads/${id}/sync-replies`, {}, authHeader);
      if (data.note) toast.info(data.note);
      else if (data.new > 0) toast.success(`${data.new} new repl${data.new === 1 ? "y" : "ies"} found.`);
      else toast.info("No new replies yet.");
      loadActivity(id);
    } catch (err) {
      if (err?.response?.status === 409) { toast.error("Please connect a Gmail account first."); setSettingsOpen(true); }
      else toast.error("Could not check for replies. Please try again.");
    } finally {
      setSyncingId(null);
    }
  };

  const loadActivity = async (id) => {
    setActivity((a) => ({ ...a, [id]: { ...(a[id] || {}), loading: true } }));
    try {
      const { data } = await axios.get(`${API}/admin/leads/${id}/activity`, authHeader);
      setActivity((a) => ({ ...a, [id]: { loading: false, data } }));
    } catch (_) {
      setActivity((a) => ({ ...a, [id]: { loading: false, data: null } }));
    }
  };

  const openComposer = (lead) => {
    if (!gmail.connected) {
      toast.error("Please connect a Gmail account before sending email.");
      setSettingsOpen(true);
      return;
    }
    setComposer({
      open: true, lead,
      subject: "Re: Your Home Pro Connect Request",
      body: `Hi ${lead.name.split(" ")[0] || lead.name},\n\nThank you for your ${lead.project_type} request in ${lead.city}. `,
      sending: false,
    });
  };

  const sendEmail = async () => {
    const lead = composer.lead;
    setComposer((c) => ({ ...c, sending: true }));
    try {
      await axios.post(`${API}/admin/leads/${lead.id}/send-email`,
        { subject: composer.subject, body: composer.body }, authHeader);
      toast.success("Email sent successfully.");
      setComposer({ open: false, lead: null, subject: "", body: "", sending: false });
      loadActivity(lead.id);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error("Please connect a Gmail account before sending email.");
        setComposer((c) => ({ ...c, sending: false, open: false }));
        setSettingsOpen(true);
        refreshGmail();
      } else {
        toast.error("Email could not be sent. Please try again.");
        setComposer((c) => ({ ...c, sending: false }));
      }
    }
  };

  const counts = useMemo(() => {
    const c = { All: leads.length, New: 0, Contacted: 0, Connected: 0 };
    leads.forEach((l) => { c[l.status] = (c[l.status] || 0) + 1; });
    return c;
  }, [leads]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "All" && l.status !== statusFilter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone, l.city, l.zip_code, l.project_type, l.budget, l.timeline, l.description, l.additional_info, l.lead_number ? `#${String(l.lead_number).padStart(4, "0")}` : "", String(l.lead_number || "")]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [leads, query, statusFilter]);

  const exportCsv = () => {
    const cols = ["lead_number", "created_at", "status", "name", "email", "phone", "preferred_contact", "project_type", "city", "zip_code", "budget", "timeline", "description", "additional_info", "notes", "attachments", "utm_source", "utm_medium", "utm_campaign", "source"];
    const header = cols.join(",");
    const rows = filtered.map((l) => cols.map((c) => {
      if (c === "attachments") return csvEscape((l.attachments || []).map((a) => a.filename).join("; "));
      if (c === "lead_number") return csvEscape(l.lead_number ? `#${String(l.lead_number).padStart(4, "0")}` : "");
      return csvEscape(l[c]);
    }).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `home-pro-connect-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading || loading) {
    return <div className="min-h-screen grid place-items-center bg-[#EAF7FC]"><Loader2 className="animate-spin text-[#133955]" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#EAF7FC]">
      <header className="bg-white border-b border-[rgba(19,57,85,0.08)] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-contractors"
              onClick={() => navigate("/admin/contractors")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#54BCE6] transition-colors"
              title="Manage contractors"
            >
              <Users size={16} />
              <span className="hidden sm:inline">Contractors</span>
            </button>
            <button
              data-testid="nav-agents"
              onClick={() => navigate("/admin/agents")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#54BCE6] transition-colors"
              title="Manage real estate agents"
            >
              <Building2 size={16} />
              <span className="hidden sm:inline">Agents</span>
            </button>
            <button
              data-testid="nav-repair-requests"
              onClick={() => navigate("/admin/repair-requests")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#54BCE6] transition-colors"
              title="Agent repair-quote requests"
            >
              <Wrench size={16} />
              <span className="hidden sm:inline">Repair Requests</span>
            </button>
            <button
              data-testid="nav-activity"
              onClick={() => navigate("/admin/activity")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#54BCE6] transition-colors"
              title="Audit log of all lead and repair activity"
            >
              <Activity size={16} />
              <span className="hidden sm:inline">Activity</span>
            </button>
            <button
              data-testid="nav-blog"
              onClick={() => navigate("/admin/blog")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#54BCE6] transition-colors"
              title="Write and manage blog posts"
            >
              <Newspaper size={16} />
              <span className="hidden sm:inline">Blog</span>
            </button>
            <button
              data-testid="email-settings-btn"
              onClick={() => setSettingsOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[rgba(19,57,85,0.15)] px-4 py-2.5 text-sm font-bold text-[#133955] hover:border-[#54BCE6] transition-colors"
              title="Email Integration settings"
            >
              <span className={`h-2 w-2 rounded-full ${gmail.connected ? "bg-green-500" : "bg-[#173042]/30"}`} />
              <Settings size={16} />
              <span className="hidden sm:inline">{gmail.connected ? "Gmail Connected" : "Email Settings"}</span>
            </button>
            <span className="hidden md:inline text-sm text-[#173042]/70">{admin?.email}</span>
            <NotificationBell />
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors">
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#133955] tracking-tight">Project Requests</h1>
            <p className="text-[#173042]/70 mt-1" data-testid="leads-count">{filtered.length} of {leads.length} lead{leads.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="add-lead-btn" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-6 py-3 font-bold hover:bg-[#133955] hover:text-white transition-colors">
              <Plus size={18} /> Add Lead
            </button>
            <button data-testid="export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-3 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors">
              <Download size={18} /> Export CSV
            </button>
          </div>
        </div>

        <div className="relative mb-4">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#173042]/40" />
          <input data-testid="leads-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, email, city, project type…" className="hpc-input bg-white" style={{ paddingLeft: "2.75rem" }} />
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          {["All", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s.toLowerCase()}`}
              onClick={() => setStatusFilter(s)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${statusFilter === s ? "bg-[#133955] text-white" : "bg-white text-[#133955] border border-[rgba(19,57,85,0.12)] hover:bg-[#EAF7FC]"}`}
            >
              {s}
              <span className={`rounded-full px-2 py-0.5 text-xs ${statusFilter === s ? "bg-white/20 text-white" : "bg-[#EAF7FC] text-[#133955]"}`}>{counts[s] ?? 0}</span>
            </button>
          ))}
        </div>

        {error && <p className="text-red-600 mb-4">{error}</p>}

        {filtered.length === 0 ? (
          <div className="rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-16 text-center">
            <Inbox size={40} className="mx-auto text-[#54BCE6] mb-4" />
            <p className="font-display text-xl font-bold text-[#133955]">No leads found</p>
            <p className="text-[#173042]/60 mt-1">{leads.length === 0 ? "New project requests will appear here." : "Try a different search."}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leads-list">
            {filtered.map((l) => {
              const open = openId === l.id;
              return (
                <div key={l.id} data-testid={`lead-row-${l.id}`} className="rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] overflow-hidden">
                  <button onClick={() => openLead(l.id, l.notes)} className="w-full flex items-center gap-4 p-5 text-left hover:bg-[#EAF7FC]/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        {l.lead_number ? <span className="font-mono text-sm font-bold text-[#54BCE6]" data-testid={`lead-number-${l.id}`}>#{String(l.lead_number).padStart(4, "0")}</span> : null}
                        <span className="font-display font-bold text-[#133955] text-lg">{l.name}</span>
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_STYLES[l.status] || STATUS_STYLES.New}`} data-testid={`lead-status-badge-${l.id}`}>{l.status}</span>
                        <span className="text-xs font-bold uppercase tracking-wide text-[#54BCE6]">{l.project_type}</span>
                        {l.attachments?.length > 0 && <span className="inline-flex items-center gap-1 text-xs text-[#173042]/60"><Paperclip size={12} />{l.attachments.length}</span>}
                        {l.notes?.trim() && <span className="inline-flex items-center gap-1 text-xs text-[#173042]/60" data-testid={`lead-has-notes-${l.id}`}><StickyNote size={12} />Note</span>}
                        {assignedOf(l).length > 0 && (
                          <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`lead-assigned-${l.id}`}>
                            {assignedOf(l).map((e) => (
                              <span key={e.contractor_id} role="button" tabIndex={0} onClick={(ev) => { ev.stopPropagation(); navigate(`/admin/contractors/${e.contractor_id}`); }} onKeyDown={(ev) => { if (ev.key === "Enter") { ev.stopPropagation(); navigate(`/admin/contractors/${e.contractor_id}`); } }} className="inline-flex items-center gap-1 text-xs font-bold text-[#0f6b3d] hover:underline cursor-pointer" title="View contractor profile" data-testid={`lead-assigned-${l.id}-${e.contractor_id}`}><UserCheck size={12} />{e.contractor_ref}{e.contractor_name ? ` · ${e.contractor_name}` : ""}</span>
                            ))}
                          </span>
                        )}
                        {l.stale && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#FBD5D5] text-[#9b1c1c]" data-testid={`lead-stale-${l.id}`}><AlertTriangle size={12} />UPDATE REQUIRED{l.days_since_update != null ? ` · ${l.days_since_update}d` : ""}</span>}
                      </div>
                      <p className="text-sm text-[#173042]/70 mt-0.5 truncate">{l.city}{l.zip_code ? ` ${l.zip_code}` : ""} · {l.email} · {l.phone}</p>
                    </div>
                    <span className="text-xs text-[#173042]/50 whitespace-nowrap hidden sm:block">{fmtDate(l.created_at)}</span>
                    <ChevronDown size={20} className={`text-[#133955] transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-6 pt-1 border-t border-[#EAF7FC]" data-testid={`lead-detail-${l.id}`}>
                      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-4 text-sm">
                        <Detail label="Preferred contact" value={l.preferred_contact || "—"} />
                        <Detail label="Budget" value={l.budget || "—"} />
                        <Detail label="Timeline" value={l.timeline || "—"} />
                        <Detail label="Source / campaign" value={[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" / ") || l.source || "Direct"} />
                      </div>
                      <div className="mt-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-1">Description</p>
                        <p className="text-[#173042]/80 whitespace-pre-wrap">{l.description}</p>
                      </div>
                      {l.additional_info && (
                        <div className="mt-4">
                          <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-1">Additional info</p>
                          <p className="text-[#173042]/80 whitespace-pre-wrap">{l.additional_info}</p>
                        </div>
                      )}
                      <div className="mt-5 rounded-xl bg-[#EAF7FC] p-4" data-testid={`assign-block-${l.id}`}>
                        <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2 flex items-center gap-1.5"><UserCheck size={13} /> Contractor Assignment</p>
                        {assignedOf(l).length > 0 ? (
                          <div className="space-y-2 mb-3" data-testid={`assigned-list-${l.id}`}>
                            {assignedOf(l).map((e) => (
                              <div key={e.contractor_id} className="flex flex-wrap items-center gap-3" data-testid={`assigned-row-${l.id}-${e.contractor_id}`}>
                                <span className="font-semibold text-[#133955]" data-testid={`assigned-name-${l.id}-${e.contractor_id}`}>
                                  <span className="font-mono text-[#54BCE6]">{e.contractor_ref}</span> · {e.contractor_name}
                                </span>
                                {roleEdit === `${l.id}:${e.contractor_id}` ? (
                                  <span className="inline-flex items-center gap-1.5">
                                    <input
                                      data-testid={`role-edit-input-${l.id}-${e.contractor_id}`}
                                      value={roleDraft}
                                      onChange={(ev) => setRoleDraft(ev.target.value)}
                                      maxLength={80}
                                      placeholder="Role / scope"
                                      className="hpc-input bg-white !py-1 !px-2 text-xs w-40"
                                    />
                                    <button data-testid={`role-save-${l.id}-${e.contractor_id}`} onClick={() => saveRoleLabel(l.id, e.contractor_id)} disabled={assigning === l.id} className="inline-flex items-center gap-1 rounded-full bg-[#133955] text-white px-3 py-1 text-xs font-bold hover:bg-[#54BCE6] hover:text-[#133955] disabled:opacity-50"><Check size={12} /> Save</button>
                                    <button data-testid={`role-cancel-${l.id}-${e.contractor_id}`} onClick={() => setRoleEdit(null)} className="text-xs font-bold text-[#173042]/60 hover:text-[#9b1c1c]">Cancel</button>
                                  </span>
                                ) : (
                                  <>
                                    {e.role_label && <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-[#FFE8B0] text-[#7a5b00]" data-testid={`assigned-role-${l.id}-${e.contractor_id}`}>{e.role_label}</span>}
                                    <button data-testid={`role-edit-${l.id}-${e.contractor_id}`} onClick={() => { setRoleEdit(`${l.id}:${e.contractor_id}`); setRoleDraft(e.role_label || ""); }} className="inline-flex items-center gap-1 text-xs font-semibold text-[#54BCE6] hover:text-[#133955]" title={e.role_label ? "Edit role" : "Add role"}>
                                      <Pencil size={11} /> {e.role_label ? "Edit role" : "Add role"}
                                    </button>
                                  </>
                                )}
                                {e.acknowledged && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0f6b3d]" data-testid={`assigned-ack-${l.id}-${e.contractor_id}`}><Check size={12} /> Acknowledged</span>}
                                <button
                                  data-testid={`unassign-btn-${l.id}-${e.contractor_id}`}
                                  onClick={() => unassignLead(l.id, e.contractor_id)}
                                  disabled={assigning === l.id}
                                  className="inline-flex items-center gap-1 rounded-full border-2 border-[rgba(19,57,85,0.2)] text-[#133955] px-3 py-1 text-xs font-bold hover:border-[#9b1c1c] hover:text-[#9b1c1c] transition-colors disabled:opacity-50"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            {l.project_status && (
                              <div className="flex flex-wrap items-center gap-3">
                                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${PROJECT_STATUS_STYLE}`} data-testid={`project-status-${l.id}`}>{l.project_status}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-[#173042]/60 mb-3">Not assigned to a contractor yet.</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          {(() => {
                            const ranked = approved
                              .map((c) => ({ c, fit: fitInfo(c, l) }))
                              .sort((a, b) => (b.fit.best - a.fit.best) || (b.fit.any - a.fit.any) || (a.c.contractor_ref || "").localeCompare(b.c.contractor_ref || ""));
                            const shown = (fitOnly[l.id] ? ranked.filter((r) => r.fit.any) : ranked)
                              .filter((r) => !assignedOf(l).some((e) => e.contractor_id === r.c.id));
                            const tag = (f) => f.best ? "★ Best fit — " : f.area ? "◆ Area — " : f.service ? "◆ Service — " : "";
                            return (
                              <select
                                data-testid={`assign-select-${l.id}`}
                                value={assignSel[l.id] ?? ""}
                                onChange={(e) => setAssignSel((s) => ({ ...s, [l.id]: e.target.value }))}
                                className="hpc-input bg-white max-w-xs"
                              >
                                <option value="">{approved.length ? "Choose a contractor…" : "No approved contractors yet"}</option>
                                {shown.map(({ c, fit }) => (
                                  <option key={c.id} value={c.id}>{tag(fit)}{c.contractor_ref} · {c.dba_name || c.legal_name}</option>
                                ))}
                              </select>
                            );
                          })()}
                          <input
                            data-testid={`assign-role-input-${l.id}`}
                            value={assignRole[l.id] ?? ""}
                            onChange={(e) => setAssignRole((s) => ({ ...s, [l.id]: e.target.value }))}
                            placeholder="Role / scope (optional)"
                            list={`role-suggestions-${l.id}`}
                            maxLength={80}
                            className="hpc-input bg-white w-48"
                          />
                          <datalist id={`role-suggestions-${l.id}`}>
                            {[l.project_type, ...((approved.find((c) => c.id === assignSel[l.id])?.services) || [])].filter(Boolean).map((s) => <option key={s} value={s} />)}
                          </datalist>
                          <button
                            data-testid={`assign-btn-${l.id}`}
                            onClick={() => assignLead(l.id)}
                            disabled={assigning === l.id || !assignSel[l.id]}
                            className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50"
                          >
                            {assigning === l.id && <Loader2 size={14} className="animate-spin" />}
                            {assignedOf(l).length ? "Add contractor" : "Assign"}
                          </button>
                        </div>
                        {assignedOf(l).length > 0 && (
                          <p className="text-xs text-[#173042]/60 mt-2" data-testid={`multi-assign-hint-${l.id}`}>You can assign multiple contractors to the same project — each one is notified and gets portal access. Removing one revokes their access immediately.</p>
                        )}
                        <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-[#133955] cursor-pointer">
                          <input type="checkbox" data-testid={`fit-only-${l.id}`} checked={!!fitOnly[l.id]} onChange={(e) => setFitOnly((s) => ({ ...s, [l.id]: e.target.checked }))} />
                          Show best-fit only (matches this project's city &amp; service)
                        </label>
                        {(projectData[l.id]?.contracts?.length > 0 || projectData[l.id]?.quotes?.length > 0) && (
                          <div className="mt-3 pt-3 border-t border-[rgba(19,57,85,0.1)] text-sm" data-testid={`project-summary-${l.id}`}>
                            {projectData[l.id]?.quotes?.length > 0 && (
                              <p className="text-[#173042]/80"><span className="font-semibold text-[#133955]">Latest quote:</span> {money(projectData[l.id].quotes[0].amount)} <span className="text-xs text-[#173042]/50">({projectData[l.id].quotes.length} total)</span></p>
                            )}
                            {projectData[l.id]?.contracts?.length > 0 && (
                              <p className="text-[#173042]/80 mt-0.5">
                                <span className="font-semibold text-[#133955]">Contract:</span> {money(projectData[l.id].contracts[0].contract_value)} ·
                                <span className="text-[#0f6b3d] font-semibold"> Referral {projectData[l.id].contracts[0].referral_percentage ?? Math.round((projectData[l.id].contracts[0].referral_rate || 0) * 100)}% = {money(projectData[l.id].contracts[0].referral_fee)}</span>
                                {projectData[l.id].contracts[0].document && <a href={`${API}/files/${projectData[l.id].contracts[0].document.file_id}?auth=${token}`} target="_blank" rel="noreferrer" className="ml-2 text-[#54BCE6] hover:underline text-xs">view doc</a>}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      {l.attachments?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Photos &amp; plans</p>
                          <div className="flex flex-wrap gap-3" data-testid={`lead-attachments-${l.id}`}>
                            {l.attachments.map((a, i) => {
                              const isImg = (a.content_type || "").startsWith("image/") || (a.data_url || "").startsWith("data:image");
                              return (
                                <a key={i} href={a.data_url} target="_blank" rel="noreferrer" className="group relative block w-24 h-24 rounded-xl overflow-hidden border border-[rgba(19,57,85,0.12)] bg-[#EAF7FC]">
                                  {isImg ? (
                                    <img src={a.data_url} alt={a.filename} className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="w-full h-full grid place-items-center text-[#133955]"><Paperclip size={20} /></span>
                                  )}
                                  <span className="absolute inset-0 bg-[#133955]/0 group-hover:bg-[#133955]/40 transition-colors grid place-items-center opacity-0 group-hover:opacity-100"><ExternalLink size={18} className="text-white" /></span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {(l.documents || []).length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-bold uppercase tracking-wide text-[#133955] mb-2">Photos & Documents</p>
                          <AttachmentList docs={l.documents} token={token} testid={`lead-documents-${l.id}`} />
                        </div>
                      )}
                      <div className="mt-5">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-bold uppercase tracking-wide text-[#133955] flex items-center gap-1.5"><StickyNote size={13} /> Notes <span className="normal-case font-semibold text-[#173042]/50">(shared with assigned contractors)</span></p>
                          {noteSaved === l.id && <span className="text-xs font-semibold text-green-600 flex items-center gap-1" data-testid={`notes-saved-${l.id}`}><Check size={13} /> Saved</span>}
                        </div>
                        <textarea
                          data-testid={`notes-input-${l.id}`}
                          rows={3}
                          value={noteDrafts[l.id] ?? l.notes ?? ""}
                          onChange={(e) => setNoteDrafts((d) => ({ ...d, [l.id]: e.target.value }))}
                          placeholder="Jot call outcomes, next steps, or details for assigned contractors…"
                          className="hpc-input bg-white resize-none"
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <button
                            data-testid={`notes-save-${l.id}`}
                            onClick={() => saveNotes(l.id)}
                            disabled={noteSaving === l.id || (noteDrafts[l.id] ?? l.notes ?? "") === (l.notes ?? "")}
                            className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50"
                          >
                            {noteSaving === l.id && <Loader2 size={14} className="animate-spin" />}
                            {noteSaving === l.id ? "Saving…" : "Save Notes"}
                          </button>
                          <span className="text-xs text-[#173042]/50">Only visible to signed-in admins.</span>
                        </div>
                      </div>

                      <div className="mt-5 flex flex-wrap items-center gap-4">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-[#133955]/60 mb-2">Lead status</p>
                          <div className="inline-flex rounded-full bg-[#EAF7FC] p-1" data-testid={`status-control-${l.id}`}>
                            {STATUSES.map((s) => (
                              <button
                                key={s}
                                data-testid={`set-status-${l.id}-${s.toLowerCase()}`}
                                disabled={savingId === l.id}
                                onClick={() => updateStatus(l.id, s)}
                                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors disabled:opacity-60 ${l.status === s ? "bg-[#133955] text-white" : "text-[#133955] hover:bg-white"}`}
                              >
                                {l.status === s && <Check size={14} />}
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          type="button"
                          data-testid={`reply-email-${l.id}`}
                          onClick={() => openComposer(l)}
                          className="inline-flex items-center gap-2 rounded-full bg-[#54BCE6] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors self-end"
                        >
                          <Mail size={16} /> Reply by Email
                        </button>
                      </div>

                      {/* Communication History / Activity */}
                      <div className="mt-6 pt-5 border-t border-[#EAF7FC]" data-testid={`activity-${l.id}`}>
                        <div className="flex items-center gap-2 mb-4">
                          <p className="text-xs font-bold uppercase tracking-wide text-[#133955] flex items-center gap-1.5"><Clock size={13} /> Communication History</p>
                          <button
                            data-testid={`sync-replies-${l.id}`}
                            onClick={() => syncReplies(l.id)}
                            disabled={syncingId === l.id}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50 ml-auto"
                          >
                            {syncingId === l.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                            Check for replies
                          </button>
                        </div>
                        {activity[l.id]?.loading ? (
                          <div className="flex items-center gap-2 text-sm text-[#173042]/50"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                        ) : (
                          <ol className="relative border-l-2 border-[#EAF7FC] ml-2 space-y-5">
                            {buildTimeline(l, activity[l.id]?.data).map((ev, idx) => (
                              <li key={idx} className="ml-5 relative">
                                <span className={`absolute -left-[27px] top-0.5 grid place-items-center w-4 h-4 rounded-full ${ev.dot}`}>
                                  <CircleDot size={10} className="text-white" />
                                </span>
                                <p className="text-xs text-[#173042]/50">{fmtDate(ev.at)}</p>
                                <p className="text-sm font-semibold text-[#133955]">{ev.title}</p>
                                {ev.subtitle && <p className="text-sm text-[#173042]/70">{ev.subtitle}</p>}
                                {ev.comm && (
                                  <button
                                    data-testid={`view-email-${ev.comm.id}`}
                                    onClick={() => setViewMsg(ev.comm)}
                                    className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#54BCE6] hover:underline"
                                  >
                                    <FileText size={12} /> View message
                                  </button>
                                )}
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Email Integration settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="email-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#133955] text-2xl flex items-center gap-2"><Mail size={20} /> Email Integration</DialogTitle>
            <DialogDescription className="text-[#173042]/70">
              Connect a Gmail account to send emails to homeowners directly from this dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {!gmail.configured ? (
              <div className="rounded-xl bg-[#FFF6E5] border border-[#ffe1a3] p-4 text-sm text-[#7a5b00]">
                Gmail is not configured on the server yet. Add your Google OAuth credentials
                (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET) and redeploy to enable this feature.
              </div>
            ) : gmail.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 rounded-xl bg-[#EAF7FC] p-4">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                  <div>
                    <p className="text-sm font-bold text-[#133955]">Connected</p>
                    <p className="text-sm text-[#173042]/70" data-testid="connected-gmail">{gmail.gmail_address}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button data-testid="gmail-reconnect" onClick={connectGmail} disabled={gmailBusy} className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-5 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors disabled:opacity-50">
                    Reconnect
                  </button>
                  <button data-testid="gmail-disconnect" onClick={disconnectGmail} disabled={gmailBusy} className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-600 px-5 py-2.5 text-sm font-bold hover:bg-red-100 transition-colors disabled:opacity-50">
                    {gmailBusy && <Loader2 size={14} className="animate-spin" />} Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button data-testid="gmail-connect" onClick={connectGmail} disabled={gmailBusy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#133955] text-white px-6 py-3.5 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-60">
                {gmailBusy ? <Loader2 size={18} className="animate-spin" /> : <Plug size={18} />}
                {gmailBusy ? "Redirecting to Google…" : "Connect Gmail"}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email composer */}
      <Dialog open={composer.open} onOpenChange={(o) => !o && setComposer((c) => ({ ...c, open: false }))}>
        <DialogContent className="max-w-2xl bg-white" data-testid="email-composer">
          <DialogHeader>
            <DialogTitle className="font-display text-[#133955] text-2xl">Reply by Email</DialogTitle>
            <DialogDescription className="text-[#173042]/70">
              Sending as {gmail.gmail_address || "your connected Gmail"} · to {composer.lead?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">To</label>
              <input data-testid="composer-to" value={composer.lead?.email || ""} readOnly className="hpc-input bg-[#EAF7FC]/60 cursor-not-allowed" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Subject</label>
              <input data-testid="composer-subject" value={composer.subject} onChange={(e) => setComposer((c) => ({ ...c, subject: e.target.value }))} className="hpc-input bg-white" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Message</label>
              <textarea data-testid="composer-body" rows={8} value={composer.body} onChange={(e) => setComposer((c) => ({ ...c, body: e.target.value }))} className="hpc-input bg-white resize-none" />
            </div>
          </div>
          <DialogFooter className="gap-3">
            <button data-testid="composer-cancel" onClick={() => setComposer((c) => ({ ...c, open: false }))} className="rounded-full border-2 border-[#133955] text-[#133955] px-6 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors">
              Cancel
            </button>
            <button data-testid="composer-send" onClick={sendEmail} disabled={composer.sending || !composer.subject.trim() || !composer.body.trim()} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50">
              {composer.sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {composer.sending ? "Sending…" : "Send Email"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View sent message */}
      <Dialog open={!!viewMsg} onOpenChange={(o) => !o && setViewMsg(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="view-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#133955] text-xl">{viewMsg?.subject}</DialogTitle>
            <DialogDescription className="text-[#173042]/60 text-sm">
              {viewMsg && `From ${viewMsg.sender} → ${viewMsg.recipient} · ${fmtDate(viewMsg.created_at)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap text-[#173042] text-[15px] leading-relaxed">
            {viewMsg?.body}
          </div>
        </DialogContent>
      </Dialog>

      <AddLeadDialog open={addOpen} onClose={() => setAddOpen(false)} onCreate={createLead} />
    </div>
  );
}

// Admin: manually add a lead (phone / walk-in / referral)
const EMPTY_LEAD = { name: "", phone: "", email: "", project_type: "", city: "", zip_code: "", preferred_contact: "", budget: "", timeline: "", description: "", additional_info: "" };

function AddLeadDialog({ open, onClose, onCreate }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.project_type || !form.city) {
      toast.error("Name, phone, project type, and city are required.");
      return;
    }
    setBusy(true);
    try {
      await onCreate(form, files);
      setForm(EMPTY_LEAD);
      setFiles([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the lead.");
    } finally { setBusy(false); }
  };

  const sel = (key, label, options, required = false) => (
    <div>
      <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">{label}{required && " *"}</label>
      <select data-testid={`addlead-${key}`} value={form[key]} onChange={set(key)} className="hpc-input bg-white">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setForm(EMPTY_LEAD); } }}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#133955] text-2xl">Add Lead Manually</DialogTitle>
          <DialogDescription className="text-[#173042]/70">
            For phone calls, walk-ins, or referrals. No emails are sent — assign it like any other lead afterward.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-1">
          <div>
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Name *</label>
            <input data-testid="addlead-name" value={form.name} onChange={set("name")} className="hpc-input bg-white" placeholder="Full name" />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Phone *</label>
            <input data-testid="addlead-phone" value={form.phone} onChange={set("phone")} className="hpc-input bg-white" placeholder="(843) 555-0100" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Email <span className="normal-case font-semibold text-[#173042]/50">(optional)</span></label>
            <input data-testid="addlead-email" type="email" value={form.email} onChange={set("email")} className="hpc-input bg-white" placeholder="name@email.com" />
          </div>
          {sel("project_type", "Project Type", PROJECT_TYPES, true)}
          <div>
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">City *</label>
            <input
              data-testid="addlead-city"
              list="addlead-city-options"
              value={form.city}
              onChange={set("city")}
              className="hpc-input bg-white"
              placeholder="Select or type a city"
            />
            <datalist id="addlead-city-options">
              {CITIES.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">ZIP</label>
            <input data-testid="addlead-zip" value={form.zip_code} onChange={set("zip_code")} className="hpc-input bg-white" placeholder="29401" maxLength={10} />
          </div>
          {sel("preferred_contact", "Preferred Contact", CONTACT_METHODS)}
          {sel("budget", "Budget", BUDGETS)}
          {sel("timeline", "Timeline", TIMELINES)}
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Project Description</label>
            <textarea data-testid="addlead-description" rows={3} value={form.description} onChange={set("description")} className="hpc-input bg-white resize-none" placeholder="What did they ask for?" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Internal Notes</label>
            <textarea data-testid="addlead-notes" rows={2} value={form.additional_info} onChange={set("additional_info")} className="hpc-input bg-white resize-none" placeholder="Anything your team should know…" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-[#133955] mb-1.5 uppercase tracking-wide">Photos & Documents <span className="normal-case font-semibold text-[#173042]/50">(optional, up to 8)</span></label>
            <input data-testid="addlead-files" type="file" multiple accept="image/*,.pdf,.doc,.docx,.csv,.txt"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="block w-full text-sm text-[#173042] file:mr-3 file:rounded-full file:border-0 file:bg-[#133955] file:text-white file:px-4 file:py-2 file:text-sm file:font-bold hover:file:bg-[#54BCE6] hover:file:text-[#133955] file:cursor-pointer" />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="addlead-files-list">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF7FC] text-[#133955] px-3 py-1 text-xs font-semibold">
                    <Paperclip size={12} /> {f.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-3">
          <button data-testid="addlead-cancel" onClick={onClose} className="rounded-full border-2 border-[#133955] text-[#133955] px-6 py-2.5 text-sm font-bold hover:bg-[#133955] hover:text-white transition-colors">Cancel</button>
          <button data-testid="addlead-submit" onClick={submit} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-6 py-2.5 text-sm font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {busy ? "Adding…" : "Add Lead"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Merge lead creation + activities into a chronological timeline.
function buildTimeline(lead, data) {
  const events = [];
  const createdAt = data?.lead_created_at || lead.created_at;
  events.push({ at: createdAt, title: "Lead received", subtitle: `${lead.project_type} in ${lead.city}`, dot: "bg-[#54BCE6]" });
  const comms = (data?.communications) || [];
  (data?.activities || []).forEach((a) => {
    if (a.type === "status_changed") {
      events.push({ at: a.created_at, title: a.description || "Status changed", dot: "bg-[#173042]" });
    } else if (a.type === "email_sent") {
      const comm = comms.find((c) => c.id === a.meta?.communication_id);
      events.push({ at: a.created_at, title: a.description || "Email sent", subtitle: a.meta?.subject ? `Subject: ${a.meta.subject}` : "", comm, dot: "bg-green-500" });
    } else if (a.type === "email_received") {
      const comm = comms.find((c) => c.id === a.meta?.communication_id);
      events.push({ at: a.created_at, title: a.description || "Reply received", subtitle: a.meta?.subject ? `Subject: ${a.meta.subject}` : "", comm, dot: "bg-[#54BCE6]" });
    } else {
      events.push({ at: a.created_at, title: a.description, dot: "bg-[#173042]" });
    }
  });
  events.sort((x, y) => new Date(x.at) - new Date(y.at));
  return events;
}

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-[#133955]/60">{label}</p>
    <p className="text-[#173042] font-semibold">{value}</p>
  </div>
);
